/**
 * Client-side translation helper backed by i18n-js.
 *
 * Translations are loaded from a static JSON file generated by
 * `bin/run rake i18n:js:export`. The locale is read from the
 * <html lang="..."> attribute set by the server.
 *
 * Usage:
 *   import { i18n } from '@/lib/i18n'
 *
 *   // Simple lookup (dot-notation matching YAML namespace)
 *   i18n.t('expanding.expand_all')
 *
 *   // Interpolation (uses Rails %{variable} syntax)
 *   i18n.t('service_timer.overdue_by', { minutes: 5 })
 *
 *   // Pluralization (key must have one/other/zero sub-keys in YAML)
 *   i18n.t('bed_bulk_actions.beds_selected', { count: 3 })
 */

import { I18n } from "i18n-js"
import { Turbo } from "@hotwired/turbo-rails"
import en from "../translations/en.json"
import es from "../translations/es.json"

type TranslationTree = Record<string, unknown>

const translations: TranslationTree = { ...en, ...es }

for (const locale of Object.keys(translations)) {
  if (locale === "_DO_NOT_EDIT") continue

  const localeTranslations = translations[locale]
  if (!localeTranslations || typeof localeTranslations !== "object" || Array.isArray(localeTranslations)) continue

  const localeTree = localeTranslations as TranslationTree
  if (!localeTree.events) continue

  localeTree.js = {
    ...((localeTree.js as TranslationTree | undefined) || {}),
    events: localeTree.events,
  }
}

const i18n = new I18n(translations)

i18n.defaultLocale = "en"
i18n.enableFallback = true

// Set locale from the <html lang="..."> attribute
const htmlLang = document.documentElement.lang
if (htmlLang) {
  i18n.locale = htmlLang
}

// Re-sync locale on Turbo navigations (JS modules persist across Turbo visits,
// so a locale change via redirect won't re-evaluate the module-level code above).
// Also clear Turbo's page cache so stale server-rendered translations aren't restored.
document.addEventListener("turbo:load", () => {
  const lang = document.documentElement.lang
  if (lang && lang !== i18n.locale) {
    i18n.locale = lang
    Turbo.cache.clear()
  }
})

// Return the bare key and warn — matches previous behavior
i18n.missingTranslation.register("warn", (_i18n, scope) => {
  const key = scope instanceof Array ? scope.join(".") : scope
  console.warn(`[i18n] Missing translation: ${key}`)
  return key
})
i18n.missingBehavior = "warn"

export { i18n }
